import './shared/RowActions.css';
import './shared/EconomiaTables.css';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Dialog, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import { PharmacySelect, PharmacyMetric, PharmacyPagination, PharmacyDownloadDialog } from './shared/PharmacyUI';
import LibroVentasChart from './shared/LibroVentasChart';
import SalesReceiptDrawer from './shared/SalesReceiptDrawer';
import './shared/LibroVentas.css';

const MESES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const fmtS = (v) => `S/ ${Number(v||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtFecha = (s) => {
  if(!s) return '';
  const str = String(s).split('T')[0];
  if(str.includes('-')){
    const [yyyy,mm,dd] = str.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }
  return str;
};
const getPeriodoMaximo = () => {
  const hoy=new Date(); let anho=hoy.getFullYear(); let mes=hoy.getMonth();
  if(mes===0){mes=12;anho-=1;} return {anho,mes};
};

// ── Helpers UI ────────────────────────────────────────────────────────────────
const Lbl = ({children}) => (
  <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.07em',textTransform:'uppercase',mb:0.5}}>{children}</Typography>
);

const Sel = props => <PharmacySelect {...props} w={props.w || 165}/>;

const TH = ({children,center,right,sortKey,orden,onSort}) => {
 const activo=sortKey && orden?.campo===sortKey;
 return <th scope="col" aria-sort={sortKey ? (activo ? (orden.dir==='asc'?'ascending':'descending') : 'none') : undefined} style={{textAlign:right?'right':center?'center':'left'}}>
 {sortKey ? <button className="sales-sort" onClick={()=>onSort(sortKey)} style={{justifyContent:right?'flex-end':center?'center':'flex-start'}}><span>{children}</span><span aria-hidden="true" className={activo?'active':''}>{activo?(orden.dir==='asc'?'↑':'↓'):'↕'}</span></button> : children}
 </th>;
};
const Paginacion = PharmacyPagination;

// ── Badges ────────────────────────────────────────────────────────────────────
const PillEstado = ({val}) => {
  const ok=(val??'').toUpperCase().includes('ACEPT');
  const noEnv=/NO ENVI|PENDIENTE/.test((val??'').toUpperCase())||(val??'')==='';
  const bg  = ok?'#EDFAF2':noEnv?'#FFF8E6':'#FFF0EF';
  const bdr = ok?'#B7EFD0':noEnv?'#FFE9A0':'#FFD4D1';
  const txt = ok?'#1A6B3C':noEnv?'#8B6914':'#C0392B';
  const dot = ok?IOS.green:noEnv?IOS.orange:IOS.red;
  return (
    <Box sx={{display:'inline-flex',alignItems:'center',gap:0.5,px:0.8,py:0.2,borderRadius:'20px',bgcolor:bg,border:`1px solid ${bdr}`}}>
      <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:dot,flexShrink:0}}/>
      <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:txt,whiteSpace:'nowrap'}}>{val||'Sin enviar'}</Typography>
    </Box>
  );
};

const KpiCard = ({label,monto,color,icon,loading}) => <PharmacyMetric label={label} value={loading?'—':fmtS(monto)} color={color} icon={icon} sub="Según los filtros aplicados"/>;

// Card móvil
const esNoEnviado = (row) => {

    const obs=(row?.obsResumen??'').toUpperCase();
    return obs.includes('NO ENVI')||obs.includes('PENDIENTE')||obs==='';
  };

const RowCard = ({row,onVer,esNoEnviado}) => (
  <Box sx={{p:2,borderBottom:`1px solid ${IOS.separator}`}}>
    <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',mb:1}}>
      <Box sx={{flex:1,minWidth:0,mr:1}}>
        <Typography sx={{fontWeight:700,fontSize:'0.82rem',color:IOS.label,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'normal',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{row.razonSocial}</Typography>
        <Typography sx={{fontSize:'0.68rem',color:IOS.label2,fontFamily:'monospace'}}>{row.serie}-{row.numero} · {fmtFecha(row.periodo)}</Typography>
      </Box>
      <Typography sx={{fontWeight:800,fontSize:'0.95rem',color:IOS.label,flexShrink:0}}>{fmtS(row.total)}</Typography>
    </Box>
    <Box sx={{display:'flex',gap:0.8,flexWrap:'wrap',mb:1.2}}>
      <PillEstado val={row.obsResumen} />
      <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:IOS.bg,border:`1px solid ${IOS.separator}`}}>
        <Typography sx={{fontSize:'0.6rem',fontWeight:600,color:IOS.label2}}>{row.tipoCpe}</Typography>
      </Box>
      {row.afectacion&&<Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:IOS.bg,border:`1px solid ${IOS.separator}`}}>
        <Typography sx={{fontSize:'0.6rem',fontWeight:600,color:IOS.label2}}>{row.afectacion}</Typography>
      </Box>}
    </Box>
    <Box className="integra-row-actions" sx={{display:'flex',gap:1}}>
      <Box component="button" type="button" data-action-tone={esNoEnviado?"orange":"blue"} aria-label={esNoEnviado?"Ver error de envío":"Ver comprobante"} title={esNoEnviado?"Ver error de envío":"Ver comprobante"} onClick={onVer}
        sx={{flex:1,height:30,borderRadius:'8px',
          border:`1px solid ${esNoEnviado?IOS.orange+'40':IOS.blue+'30'}`,
          bgcolor:esNoEnviado?`${IOS.orange}08`:`${IOS.blue}08`,
          cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5,
          '&:hover':{bgcolor:esNoEnviado?`${IOS.orange}14`:`${IOS.blue}14`}}}>
        {esNoEnviado
          ?<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          :<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        }
        
      </Box>
    </Box>
  </Box>
);

const Modal = ({open,onClose,children,w=460}) => <Dialog open={Boolean(open)} onClose={onClose} aria-label={React.Children.toArray(children)[0]?.props?.title || 'Detalle del reporte'} className="sales-dialog" maxWidth={false} PaperProps={{'aria-label':React.Children.toArray(children)[0]?.props?.title || 'Detalle del reporte',sx:{width:w,maxWidth:'calc(100% - 32px)',borderRadius:'24px',m:2}}} sx={{zIndex:15000}}>{children}</Dialog>;

const ModalHeader = ({title,onClose,icon,color=IOS.blue}) => (
  <Box sx={{px:2.5,pt:2.5,pb:2,borderBottom:`1px solid ${IOS.separator}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
    <Box sx={{display:'flex',alignItems:'center',gap:1.2}}>
      <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>{icon}</Box>
      <Typography sx={{fontWeight:700,fontSize:'0.92rem',color:IOS.label}}>{title}</Typography>
    </Box>
    <Box component="button" className="sales-modal-close" aria-label="Cerrar ventana" onClick={onClose}
      sx={{width:28,height:28,borderRadius:'50%',border:'none',bgcolor:'#E5E5EA',cursor:'pointer',
        display:'flex',alignItems:'center',justifyContent:'center','&:hover':{bgcolor:'#D1D1D6'}}}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
    </Box>
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
const LibroVentas = ({modulo='economia'}) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const ueId = user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0;
  const {anho:anhoMax,mes:mesMax} = getPeriodoMaximo();

  const [anho,setAnhoRaw] = useState(anhoMax);
  const [mes,setMes]      = useState(mesMax);
  const [busqueda,setBusqueda] = useState('');
  const [cargando,setCargando] = useState(true);
  const [errorDatos,setErrorDatos]=useState('');
  const [errorGrafico,setErrorGrafico]=useState('');
  const [cargandoGrafico,setCargandoGrafico]=useState(true);
  const datosRequest=useRef(0), graficoRequest=useRef(0);
  const [errorAccion,setErrorAccion]=useState('');
  const [datos,setDatos]       = useState([]);

  const [filtroSerie,      setFiltroSerie]       = useState('TODOS');
  const listadoRef = useRef(null);
  const [filtroEstado,     setFiltroEstado]      = useState('TODOS');
  const [filtroAfectacion, setFiltroAfectacion]  = useState('TODOS');
  const [filtroRapido,     setFiltroRapido]      = useState('TODOS');
  const [filtroTipoCpe, setFiltroTipoCpe] = useState('TODOS');
  const [pagina,setPagina] = useState(1);
  const [rpp,setRpp]       = useState(()=>window.matchMedia('(max-width: 899px)').matches?10:25);
  const [orden,setOrden]   = useState({campo:null,dir:'asc'});

  const toggleOrden = (campo) => {
    setPagina(1);
    setOrden(prev=>{
      if(prev.campo!==campo) return {campo,dir:'desc'};
      return {campo,dir:prev.dir==='desc'?'asc':'desc'};
    });
  };

  const [datosGrafico,setDatosGrafico] = useState({dias:[],caja:[],sismed:[]});
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [tipoGrafico, setTipoGrafico] = useState('line');
  const [modalExportar,setModalExportar] = useState(false);
  const [exportando,setExportando]       = useState(false);
  const [modalFormato,setModalFormato]   = useState(false);
  const [filaImprimir,setFilaImprimir]   = useState(null);
  const [imprimiendo,setImprimiendo]     = useState(false);
  const [panelOpen,setPanelOpen]         = useState(false);

  const [filaSelec,setFilaSelec]         = useState(null);
  const [cdrDisponible,setCdrDisponible] = useState(false);
  const [descargandoDoc,setDescargandoDoc] = useState(null); // 'xml' | 'cdr' | null
  const [detalleVenta,setDetalleVenta]   = useState([]);
  const [cargandoDet,setCargandoDet]     = useState(false);
  const detailRequest=useRef(0);
  const [errorDetalle,setErrorDetalle]=useState('');
  const [errorEnvio,setErrorEnvio]=useState('');
  const [errorDocumento,setErrorDocumento]=useState('');
  const [errorImpresion,setErrorImpresion]=useState('');
  const [modalNoEnviado,setModalNoEnviado]       = useState(false);
  const [detalleNoEnviado,setDetalleNoEnviado]   = useState(null);
  const [cargandoNoEnviado,setCargandoNoEnviado] = useState(false);
  const [filaNoEnviado,setFilaNoEnviado]         = useState(null);

  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const handleAnho = (v) => {
    const n=Number(v); if(!Number.isInteger(n)||n<2020||n>anhoMax) return;
    if(n===anhoMax&&mes>mesMax) setMes(mesMax);
    setAnhoRaw(n);
  };
  const handleMes = (v) => {
    const n=Number(v); if(anho===anhoMax&&n>mesMax) return; setMes(n);
  };

  const cargarDatos = useCallback(async () => {
    const request=++datosRequest.current; setCargando(true); setErrorDatos('');
    try {
      const r=await api.get('/economia/LibroVentas/consolidado',{params:{periodo:anho,mes,ueId,busqueda:busqueda||null}});
      if(!r.data.success) throw new Error('consulta');
      if(request===datosRequest.current) setDatos(r.data.data || []);
    } catch(e){if(request===datosRequest.current){setDatos([]);setErrorDatos('No pudimos cargar el libro de ventas. Intenta nuevamente.');}}
    finally{if(request===datosRequest.current)setCargando(false);}
  },[anho,mes,ueId,busqueda]);
  const cargarGrafico = useCallback(async () => {
    const request=++graficoRequest.current;setCargandoGrafico(true);setErrorGrafico('');
    try {
      const r=await api.get('/economia/LibroVentas/grafico-caja-sismed',{params:{periodo:anho,mes,ueId}});
      if(!r.data.success)throw new Error('consulta');
      const rows=r.data.data || [];
      if(request===graficoRequest.current)setDatosGrafico({dias:rows.map(x=>String(x.dia??'')),caja:rows.map(x=>Number(x.totalCaja||0)),sismed:rows.map(x=>Number(x.totalSismed||0))});
    }catch(e){if(request===graficoRequest.current)setErrorGrafico('No pudimos cargar la recaudación diaria.');}
    finally{if(request===graficoRequest.current)setCargandoGrafico(false);}
  },[anho,mes,ueId]);
  useEffect(()=>{setPagina(1);setCargando(true);const timer=setTimeout(cargarDatos,250);return()=>{clearTimeout(timer);++datosRequest.current;};},[cargarDatos]);
  useEffect(()=>{setDiaSeleccionado(null);cargarGrafico();return()=>{++graficoRequest.current;};},[cargarGrafico]);

  const optsSerie      = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.serie).filter(Boolean))],[datos]);
  const optsEstado     = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.obsResumen).filter(Boolean))],[datos]);
  const optsAfectacion = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.afectacion).filter(Boolean))],[datos]);
  
  const mesesDisponibles = MESES.map((m,i)=>({label:m,value:i+1})).filter(m=>anho<anhoMax||m.value<=mesMax);

  const datosFiltrados = useMemo(()=>{
    let f=datos;

    // Filtro texto local
    if(busqueda.trim()){
      const q=busqueda.trim().toLowerCase();
      f=f.filter(d=>
        String(d.razonSocial||'').toLowerCase().includes(q)||
        String(d.ruc||'').toLowerCase().includes(q)||
        String(d.numero||'').toLowerCase().includes(q)
      );
    }
      
    if(filtroRapido==='FACTURAS') f=f.filter(d=>String(d.td||'').trim().padStart(2,'0')==='01');
    if(filtroRapido==='BOLETAS')  f=f.filter(d=>String(d.td||'').trim().padStart(2,'0')==='03');
    if(filtroRapido==='NOTAS')    f=f.filter(d=>String(d.td||'').trim().padStart(2,'0')==='07');
    if(filtroSerie!=='TODOS')      f=f.filter(d=>d.serie===filtroSerie);
    if(filtroEstado!=='TODOS') f=f.filter(d=>d.obsResumen===filtroEstado);
    if(filtroAfectacion!=='TODOS') f=f.filter(d=>d.afectacion===filtroAfectacion);
    if(filtroTipoCpe!=='TODOS') f=f.filter(d=>codigoTipoCPE(d)===filtroTipoCpe);
    
    if(diaSeleccionado){
      f = f.filter(d => {
        if(!d.periodo) return false;

        const str = String(d.periodo).split('T')[0];
        const dd = str.includes('-')
          ? str.split('-')[2]
          : str.split('/')[0];

        const ddStr = String(dd).padStart(2,'0');

        return ddStr === diaSeleccionado;
      });
    }
    return f;
  },[datos,busqueda,filtroRapido,filtroSerie,filtroEstado,filtroAfectacion,diaSeleccionado,filtroTipoCpe]);

  const CAMPOS_NUM = ['baseImponible','igv','inafecta','total'];
  const datosOrdenados = useMemo(()=>{
    if(!orden.campo) return datosFiltrados;
    const {campo,dir}=orden;
    const mult = dir==='asc'?1:-1;
    return [...datosFiltrados].sort((a,b)=>{
      if(CAMPOS_NUM.includes(campo))
        return (Number(a[campo]||0)-Number(b[campo]||0))*mult;
      const va=String(a[campo]??''), vb=String(b[campo]??'');
      return va.localeCompare(vb,'es',{numeric:true,sensitivity:'base'})*mult;
    });
  },[datosFiltrados,orden]);

const kpis = useMemo(()=>{
  const facts = datosFiltrados.filter(d=>d.td==='01');
  const bols  = datosFiltrados.filter(d=>d.td==='03');
  const notas = datosFiltrados.filter(d=>d.td==='07');

  const debitos=datosFiltrados.filter(d=>String(d.td||'').trim().padStart(2,'0')==='08');
  const sum = arr => arr.reduce((s,d)=>s+Math.abs(Number(d.total||0)),0);

    return [
      {label:`${facts.length} Facturas`,       monto:sum(facts), color:IOS.blue,
        icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h6M9 17h4"/></svg>},
      {label:`${bols.length} Boletas`,          monto:sum(bols),  color:IOS.green,
        icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="1.8" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>},
      {label:`${notas.length} Notas de crédito`,  monto:-sum(notas), color:IOS.red,
        icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 11v4M12 17h.01"/></svg>},
      {label:`${debitos.length} Notas de débito`,monto:sum(debitos),color:IOS.orange,icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 15h6M12 12v6"/></svg>},
      {label:`${datosFiltrados.length} Total`, monto:datosFiltrados.reduce((s,d)=>s+Number(d.total||0),0), color:IOS.purple,
        icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>},
    ];
  },[datosFiltrados]);

 const tipoCPETexto = (row) => {
    const td = (row?.td || '').trim();
    const map = { '01':'FACTURA', '03':'BOLETA', '07':'NOTA DE CREDITO', '08':'NOTA DE DEBITO' };
    return map[td] || row?.tipoCpe || 'BOLETA';
  };

  function codigoTipoCPE(row) {
    const td = (row?.td || '').trim();
    if (td) return td;
    const txt = (row?.tipoCpe || '').toUpperCase();
    if (txt.includes('FACTURA')) return '01';
    if (txt.includes('BOLETA')) return '03';
    if (txt.includes('CREDITO')||txt.includes('CRÉDITO')) return '07';
    if (txt.includes('DEBITO')||txt.includes('DÉBITO')) return '08';
    return '';
  }

  const OPTS_TIPO_CPE = [
    { value:'TODOS', label:'TODOS' },
    { value:'01', label:'01 FACTURA' },
    { value:'03', label:'03 BOLETA' },
    { value:'07', label:'07 NOTA DE CRÉDITO' },
    { value:'08', label:'08 NOTA DE DÉBITO' },
  ];

  const PLAZO_DIAS_SUNAT = 5;
  const dentroDePlazo = (row) => {
    const dias = row?.diasTranscurridos;
    return dias != null && dias <= PLAZO_DIAS_SUNAT;
  };

  const noEnviados = useMemo(() => datos.filter(esNoEnviado), [datos]);
  const noEnviadosDentroPlazo = useMemo(() => noEnviados.filter(dentroDePlazo), [noEnviados]);
  const noEnviadosFueraPlazo = useMemo(() => noEnviados.filter(r=>!dentroDePlazo(r)), [noEnviados]);

  const verPendientesEnTabla = () => {
    setBusqueda(''); setFiltroRapido('TODOS'); setFiltroSerie('TODOS');
    setFiltroAfectacion('TODOS'); setFiltroTipoCpe('TODOS'); setDiaSeleccionado(null);
    const estadoSinEnviar = optsEstado.find(estado => /NO\s+ENVI/i.test(String(estado)))
      ?? optsEstado.find(estado => /PENDIENTE/i.test(String(estado)));
    setFiltroEstado(estadoSinEnviar ?? 'TODOS'); setPagina(1);
    requestAnimationFrame(() => {
      listadoRef.current?.focus({preventScroll:true});
      listadoRef.current?.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
    });
  };

  const verComprobante = async (row) => {
    const request=++detailRequest.current;setErrorDetalle('');setErrorEnvio('');setErrorDocumento('');
    if(esNoEnviado(row)){
      setFilaNoEnviado(row); setDetalleNoEnviado(null); setCargandoNoEnviado(true); setModalNoEnviado(true);
      try {
        const r=await api.get('/economia/LibroVentas/detalle-no-enviado',{params:{codigo:row.codigo,ueId}});
        if(!r.data.success)throw new Error('consulta');
        if(request===detailRequest.current)setDetalleNoEnviado(r.data.data);
      } catch(e){if(request===detailRequest.current)setErrorEnvio('No pudimos consultar el estado de envío. Intenta nuevamente.');} finally{if(request===detailRequest.current)setCargandoNoEnviado(false);}
      return;
    }
    setFilaSelec(row); setPanelOpen(true); setCdrDisponible(false);
    setDetalleVenta([]); setCargandoDet(true);
    try {
      const rd = await api.get('/economia/ComprobantesPago/detalle-venta', { params:{ idVenta: row.codigo } });
      if(!rd.data.success)throw new Error('consulta');
      if(request!==detailRequest.current)return;
      setDetalleVenta(rd.data.data || []);

      // Solo verificar XML/CDR si el comprobante SÍ existe en Integra
      if(rd.data.success && rd.data.data?.length>0){
        try {
          const r = await api.get('/economia/ComprobantesPago/verificar-documentos', {
            params:{ tipoCPE: tipoCPETexto(row), serie:row.serie, numero:row.numero, ueId }
          });
          if(request===detailRequest.current && r.data.success) setCdrDisponible(!!r.data.cdrDisponible);
        } catch(e){ if(request===detailRequest.current)setCdrDisponible(false); }
      }
    } catch(e){ if(request===detailRequest.current)setErrorDetalle('No pudimos cargar el comprobante. Intenta nuevamente.'); }
    finally { if(request===detailRequest.current)setCargandoDet(false); }
  };

  const descargarDocOriginal = async (tipo) => {
    if(!filaSelec) return;
    setDescargandoDoc(tipo);setErrorDocumento('');
    try {
      const tipoCPE = tipoCPETexto(filaSelec);
      const endpoint = tipo==='xml' ? 'descargar-xml-original' : 'descargar-cdr';
      const params = { tipoCPE, serie:filaSelec.serie, numero:filaSelec.numero, ueId };
      if(tipo==='xml'){ params.idVenta = filaSelec.codigo; params.modulo = filaSelec.fuente || 'CAJA'; }
      const r = await api.get(`/economia/ComprobantesPago/${endpoint}`, { params, responseType:'blob' });
      const nombre = tipo==='xml' ? `${filaSelec.serie}-${filaSelec.numero}.xml` : `R-${filaSelec.serie}-${filaSelec.numero}.xml`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data],{type:'application/xml'}));
      a.download = nombre; a.click(); URL.revokeObjectURL(a.href);
    } catch(e){
      console.error(e);
      const msg = e?.response?.data?.message || 'No se pudo descargar el documento.';
      setErrorDocumento(typeof msg==='string'?msg:'No se pudo descargar el documento.');
    }
    finally { setDescargandoDoc(null); }
  };

  const imprimir = (fila) => { setErrorImpresion('');setFilaImprimir(fila); setModalFormato(true); };

  const ejecutarImpresion = async (fmt) => {
    if(!filaImprimir) return; setImprimiendo(true);setErrorImpresion('');
    try {
      const r=await api.get('/economia/ComprobantesPago/imprimir',{
        params:{idVenta:String(filaImprimir.codigo),fuente:filaImprimir.fuente||'CAJA',tipoCPE:tipoCPETexto(filaImprimir),formato:fmt,ueId},
        responseType:'blob'
      });
      window.open(URL.createObjectURL(new Blob([r.data],{type:'application/pdf'})),'_blank');
      setModalFormato(false);
    } catch(e){setErrorImpresion('No se pudo preparar la impresión. Intenta nuevamente.');} finally{setImprimiendo(false);}
  };

  const [modalConfirmExport,setModalConfirmExport] = useState(null); // null | tipo pendiente

  const ejecutarExportacion = async (tipo) => {
    setExportando(true);setErrorAccion('');
    try {
      const resp=await api.post(`/economia/LibroVentas/exportar-${tipo}`,{
        datos:datosFiltrados,periodo:anho,mes,mesNombre:MESES[mes-1],
        filtroRapido,filtroSerie,filtroEstado,busqueda:busqueda||null,
      },{responseType:'blob'});
      const ext=tipo==='excel'?'xlsx':'pdf';
      const mime=tipo==='excel'?'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':'application/pdf';
      const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([resp.data],{type:mime}));
      a.download=`LibroVentas_${MESES[mes-1]}_${anho}.${ext}`; a.click(); URL.revokeObjectURL(a.href);
      setModalExportar(false); setModalConfirmExport(null);
    } catch(e){setErrorAccion('No se pudo preparar el archivo. Intenta nuevamente.');} finally{setExportando(false);}
  };

  const exportarConFiltros = (tipo) => {
    if(noEnviados.length>0){ setModalConfirmExport(tipo); return; }
    ejecutarExportacion(tipo);
  };

  const paginaActual=Math.min(pagina,Math.max(1,Math.ceil(datosOrdenados.length/rpp)));
  const datosPagina=datosOrdenados.slice((paginaActual-1)*rpp,paginaActual*rpp);

  return (
    <>
      <Box className="sales-page pharmacy-page" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
        <HeaderIOS titulo="Libro de Ventas" subtitulo="Economía"
          onHome={()=>navigate(`/${modulo}`)} onLogout={handleLogout}
          nombreCompleto={user?.nombreCompleto||user?.usuario||''}
          avatarLetra={(user?.nombreCompleto||user?.usuario||'U').charAt(0).toUpperCase()} />

        <Box className="sales-content" sx={{maxWidth:1600,mx:'auto',px:{xs:1.5,sm:2.6},pt:2.5,pb:6}}>
          <div className="sales-intro"><div><span>ECONOMÍA · REGISTRO MENSUAL</span><h1>Libro de ventas</h1><p>Revisa tu recaudación y cada comprobante, en un solo lugar.</p></div><span className="sales-period-badge">{MESES[mes-1]} {anho}</span></div>

          {/* KPIs + Periodo */}
          <Box className="sales-summary">
            {/* Periodo — bloqueado a mes anterior */}
            <Box sx={{bgcolor:IOS.surface,borderRadius:'24px',p:2,boxShadow:'0 8px 28px rgba(34,55,80,0.045)',border:`1px solid ${IOS.separator}`,gridColumn:{xs:'1 / -1',sm:'auto'}}}>
              <Lbl>Periodo Cerrado</Lbl>
              <Box sx={{display:'flex',gap:1,mt:0.5,alignItems:'center'}}>
                <Sel aria-label="Año del periodo" value={anho} onChange={e=>handleAnho(e.target.value)} w={80}>
                  {Array.from({length:Math.max(1,anhoMax-2019)},(_,i)=>anhoMax-i).map(year=><option key={year} value={year}>{year}</option>)}
                </Sel>
                <Sel aria-label="Mes del periodo" value={mes} onChange={e=>handleMes(Number(e.target.value))} w={110}>
                  {mesesDisponibles.map(m=><option key={m.value} value={m.value}>{m.label.slice(0,3)}</option>)}
                </Sel>
              </Box>
            </Box>
            {kpis.map(k=><KpiCard key={k.label} {...k} loading={cargando || !!errorDatos}/>)}
          </Box>

          {!cargando && !errorDatos && noEnviados.length>0 && (
            <section className="sales-closing-alert" role="status" aria-label="Comprobantes que requieren atención">
              <div className="sales-closing-count"><strong>{noEnviados.length}</strong><span>sin enviar</span></div>
              <div className="sales-closing-message"><span className="sales-closing-eyebrow">REQUIERE ATENCIÓN · SUNAT</span><h2>Revisa los comprobantes de este cierre</h2>
                <div className="sales-closing-tags">{noEnviadosDentroPlazo.length>0&&<span>{noEnviadosDentroPlazo.length} dentro del plazo</span>}{noEnviadosFueraPlazo.length>0&&<span className="is-overdue">{noEnviadosFueraPlazo.length} fuera de plazo</span>}</div>
                {noEnviadosFueraPlazo.length>0?<p>Si cambias su fecha al período actual, <strong>dejarán de formar parte de este libro</strong>. Revisa el impacto antes de corregirlos.</p>:<p>Revisa los pendientes para completar el envío de este período.</p>}
              </div>
              <div className="sales-closing-actions"><button type="button" className="sales-closing-action sales-closing-view" onClick={verPendientesEnTabla}>Ver en la tabla <span aria-hidden="true">↓</span></button>
              <button type="button" className="sales-closing-action" onClick={()=>navigate('/economia/monitor-envios')}>Revisar comprobantes <span aria-hidden="true">→</span></button></div>
            </section>
          )}

          {/* Gráfico */}
          <Box sx={{bgcolor:IOS.surface,borderRadius:'24px',p:2.5,mb:2,boxShadow:'0 8px 28px rgba(34,55,80,0.045)',border:`1px solid ${IOS.separator}`}}>
            <Box sx={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',mb:2,gap:1.5,flexWrap:'wrap'}}>
              <Box>
                <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label,mb:0.2}}>Recaudación Diaria — Caja vs SISMED</Typography>
                <Typography sx={{fontSize:'0.7rem',color:IOS.label2}}>{MESES[mes-1]} {anho}</Typography>
              </Box>
              <Box sx={{display:'flex',gap:0.4,bgcolor:IOS.bg,borderRadius:'10px',p:0.4,flexShrink:0}}>
                {[
                  {key:'line',   tip:'Línea',         icon:<path d="M3 17l4-8 4 4 4-6 4 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>},
                  {key:'bar',    tip:'Barras',         icon:<><rect x="3" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".9"/><rect x="10" y="6" width="4" height="12" rx="1" fill="currentColor" opacity=".9"/><rect x="17" y="3" width="4" height="15" rx="1" fill="currentColor" opacity=".9"/></>},
                  {key:'stacked',tip:'Apilado',        icon:<><rect x="3" y="13" width="4" height="5" rx="1" fill="currentColor" opacity=".5"/><rect x="3" y="8"  width="4" height="5" rx="1" fill="currentColor"/><rect x="10" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".5"/><rect x="10" y="5" width="4" height="5" rx="1" fill="currentColor"/><rect x="17" y="11" width="4" height="7" rx="1" fill="currentColor" opacity=".5"/><rect x="17" y="6" width="4" height="5" rx="1" fill="currentColor"/></>},
                  {key:'scatter',tip:'Dispersión',     icon:<><circle cx="5"  cy="15" r="1.8" fill="currentColor"/><circle cx="9"  cy="9"  r="1.8" fill="currentColor"/><circle cx="13" cy="13" r="1.8" fill="currentColor"/><circle cx="17" cy="6"  r="1.8" fill="currentColor"/><circle cx="20" cy="11" r="1.8" fill="currentColor"/></>},
                ].map(({key,tip,icon})=>(
                  <Tooltip key={key} title={tip} placement="top">
                    <Box component="button" aria-label={tip} aria-pressed={tipoGrafico===key} onClick={()=>setTipoGrafico(key)}
                      sx={{width:30,height:30,borderRadius:'8px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s',
                        bgcolor:tipoGrafico===key?IOS.surface:'transparent',
                        boxShadow:tipoGrafico===key?'0 1px 3px rgba(0,0,0,0.12)':'none',
                        color:tipoGrafico===key?IOS.blue:IOS.label2,
                        '&:hover':{bgcolor:tipoGrafico===key?IOS.surface:IOS.separator+'40'}}}>
                      <svg width="18" height="18" viewBox="0 0 24 24">{icon}</svg>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
            {cargandoGrafico ? <div className="sales-chart-state" role="status"><CircularProgress size={26}/><span>Cargando recaudación…</span></div> : errorGrafico ? <div className="sales-chart-state"><Alert severity="error" action={<button onClick={cargarGrafico}>Reintentar</button>}>{errorGrafico}</Alert></div> : <LibroVentasChart datos={datosGrafico} tipo={tipoGrafico} onDay={dia=>{setDiaSeleccionado(prev=>prev===dia?null:dia);setPagina(1);}}/>}
            <div className="sales-chart-footer"><span>Selecciona un día para ver sus comprobantes.</span><Sel aria-label="Filtrar por día" value={diaSeleccionado||''} onChange={e=>{setDiaSeleccionado(e.target.value||null);setPagina(1);}}><option value="">Todos los días</option>{datosGrafico.dias.map(d=><option key={d} value={String(d).padStart(2,'0')}>Día {String(d).padStart(2,'0')}</option>)}</Sel></div>
              {diaSeleccionado&&(
                <Box sx={{display:'flex',alignItems:'center',gap:1,mt:1}}>
                  <Typography sx={{fontSize:'0.75rem',color:IOS.label2}}>
                    Filtrando día: <strong style={{color:IOS.blue}}>{diaSeleccionado}</strong>
                  </Typography>
                  <Box component="button" onClick={()=>{setDiaSeleccionado(null);setPagina(1);}}
                    sx={{height:22,px:1,borderRadius:'6px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.bg,cursor:'pointer',fontSize:'0.68rem',color:IOS.label2}}>
                    Limpiar
                  </Box>
                </Box>
              )}
          </Box>

          {/* Filtros */}
          <Box ref={listadoRef} tabIndex={-1} aria-label="Filtros de comprobantes" className="sales-listing-filters" sx={{bgcolor:IOS.surface,borderRadius:'24px',p:2,mb:2,boxShadow:'0 8px 28px rgba(34,55,80,0.045)',border:`1px solid ${IOS.separator}`}}>
            <Box sx={{display:'flex',gap:1.5,flexWrap:'wrap',alignItems:'flex-end',mb:2}}>
              {/* Búsqueda */}
              <Box sx={{flex:'1 1 200px',maxWidth:260}}>
                <Lbl>Buscar</Lbl>
                <Box sx={{position:'relative'}}>
                  <Box sx={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:IOS.label2}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  </Box>
                  <input aria-label="Buscar comprobantes" type="text" placeholder="Cliente, DNI, número..." value={busqueda}
                    onChange={e=>setBusqueda(e.target.value)}
              
                    style={{width:'100%',height:34,paddingLeft:28,paddingRight:10,border:`1px solid ${IOS.separator}`,borderRadius:8,fontSize:'0.8rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}
                    onFocus={e=>e.target.style.borderColor=IOS.blue}
                    onBlur={e=>e.target.style.borderColor=IOS.separator} />
                </Box>
              </Box>

              {/* Tabs rápidos */}
              <Box>
                <Lbl>Tipo</Lbl>
                <Box sx={{display:'flex',gap:0.5,bgcolor:IOS.bg,borderRadius:'10px',p:0.4}}>
                  {['TODOS','FACTURAS','BOLETAS','NOTAS'].map(v=>(
                    <Box component="button" key={v} aria-pressed={filtroRapido===v} onClick={()=>{setFiltroRapido(v);setPagina(1);}}
                      sx={{px:1.2,py:0.6,borderRadius:'8px',cursor:'pointer',transition:'all 0.15s',
                        bgcolor:filtroRapido===v?IOS.surface:'transparent',
                        boxShadow:filtroRapido===v?'0 1px 3px rgba(0,0,0,0.1)':'none'}}>
                      <Typography sx={{fontSize:'0.72rem',fontWeight:filtroRapido===v?700:500,color:filtroRapido===v?IOS.blue:IOS.label2,whiteSpace:'nowrap'}}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ml:'auto',display:'flex',alignItems:'flex-end',gap:1}}>
                <Box sx={{px:1.5,py:0.6,bgcolor:IOS.bg,borderRadius:'20px',border:`1px solid ${IOS.separator}`,textAlign:'center'}}>
                  <Typography sx={{fontSize:'0.58rem',color:IOS.label2,display:'block'}}>Registros</Typography>
                  <Typography sx={{fontSize:'0.88rem',fontWeight:700,color:IOS.blue,lineHeight:1}}>{datosFiltrados.length.toLocaleString()}</Typography>
                </Box>
                <Tooltip title="Exportar" placement="top">
                  <Box component="button" aria-label="Exportar libro de ventas" disabled={cargando || !!errorDatos || !datosFiltrados.length} onClick={()=>{setErrorAccion('');setModalExportar(true);}}
  sx={{width:34,height:34,borderRadius:'50%',border:'none',bgcolor:IOS.green,
    cursor:datosFiltrados.length===0?'default':'pointer',
    display:'flex',alignItems:'center',justifyContent:'center',
    opacity:datosFiltrados.length===0?0.4:1,
    transition:'all 0.15s','&:hover':{filter:'brightness(0.9)'}}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                      <path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/>
                    </svg>
                  </Box>
                </Tooltip>
              </Box>
            </Box>

            {/* Dropdowns */}
            <Box sx={{display:'flex',flexWrap:'wrap',gap:1.5,alignItems:'flex-end',pt:1.5,borderTop:`1px solid ${IOS.separator}`}}>
              {[['Serie',filtroSerie,setFiltroSerie,optsSerie],['CDR',filtroEstado,setFiltroEstado,optsEstado],['Afectación',filtroAfectacion,setFiltroAfectacion,optsAfectacion]].map(([lbl,val,set,opts])=>(
                <Box key={lbl}>
                  <Lbl>{lbl}</Lbl>
                  <Sel aria-label={lbl} value={val} onChange={e=>{set(e.target.value);setPagina(1);}}>
                    {opts.map(o=><option key={o} value={o}>{o}</option>)}
                  </Sel>
                </Box>
              ))}
              <Box>
                <Lbl>Tipo CPE</Lbl>
                <Sel aria-label="Tipo CPE" value={filtroTipoCpe} onChange={e=>{setFiltroTipoCpe(e.target.value);setPagina(1);}}>
                  {OPTS_TIPO_CPE.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </Sel>
              </Box>
            </Box>
          </Box>

          {/* Tabla / Cards */}
          {errorDatos ? <Alert severity="error" action={<button onClick={cargarDatos}>Reintentar</button>}>{errorDatos}</Alert> : cargando
            ?<Box sx={{py:10,textAlign:'center'}}><CircularProgress sx={{color:IOS.blue}} /></Box>
            :<Box sx={{bgcolor:IOS.surface,borderRadius:'24px',boxShadow:'0 8px 28px rgba(34,55,80,0.045)',border:`1px solid ${IOS.separator}`,overflow:'hidden'}}>

              {/* Móvil */}
              <Box sx={{display:{xs:'block',sm:'none'}}}>
                {datosPagina.length===0
                  ?<Box sx={{py:6,textAlign:'center'}}><Typography sx={{color:IOS.label2,fontSize:'0.82rem'}}>Sin registros</Typography></Box>
                  :datosPagina.map((row,i)=>(
                    <RowCard key={i} row={row} esNoEnviado={esNoEnviado(row)}
                      onVer={()=>verComprobante(row)} />
                  ))
                }
              </Box>

              {/* Desktop */}
              <Box className="economia-table-scroll" sx={{display:{xs:'none',sm:'block'},overflowX:'auto',overflowY:'visible',
                '&::-webkit-scrollbar':{width:4,height:4},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                <table className="economia-data-table" aria-label="Libro de ventas">
                  <colgroup><col style={{width:"10%"}}/><col style={{width:"15%"}}/><col style={{width:"21%"}}/><col style={{width:"10%"}}/><col style={{width:"7%"}}/><col style={{width:"10%"}}/><col style={{width:"10%"}}/><col style={{width:"10%"}}/><col style={{width:"7%"}}/></colgroup>
                  <thead><tr><TH  sortKey="periodo" orden={orden} onSort={toggleOrden}>Emisión</TH><TH  sortKey="serie" orden={orden} onSort={toggleOrden}>Comprobante</TH><TH  sortKey="razonSocial" orden={orden} onSort={toggleOrden}>Cliente / documento</TH><TH right sortKey="baseImponible" orden={orden} onSort={toggleOrden}>Base imp.</TH><TH right sortKey="igv" orden={orden} onSort={toggleOrden}>IGV</TH><TH right sortKey="inafecta" orden={orden} onSort={toggleOrden}>Inafecta</TH><TH right sortKey="total" orden={orden} onSort={toggleOrden}>Total</TH><TH center sortKey="obsResumen" orden={orden} onSort={toggleOrden}>Estado</TH><TH center>Acción</TH></tr></thead>
                  <tbody>
                    {datosPagina.length===0
                      ?<tr><td colSpan={9} style={{padding:'40px',textAlign:'center',color:IOS.label2,fontSize:'0.82rem'}}>Sin registros para mostrar</td></tr>
                      :datosPagina.map((row,i)=>(
                        <tr key={i}
                          style={{borderBottom:`1px solid ${IOS.separator}`,backgroundColor:IOS.surface,transition:'background 0.1s'}}
                          onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                          onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                          <td style={{padding:'7px 8px',color:IOS.label2,whiteSpace:'nowrap',fontSize:'0.7rem'}}>{fmtFecha(row.periodo)}</td><td><strong>{row.serie}-{row.numero}</strong><small>{row.tipoCpe}</small></td><td><strong>{row.razonSocial}</strong><small>{row.ruc || "Sin documento"}</small></td><td style={{padding:'7px 8px',textAlign:'right',color:IOS.label,fontSize:'0.72rem'}}>{Number(row.baseImponible||0).toLocaleString("es-PE",{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td style={{padding:'7px 8px',textAlign:'right',color:IOS.label2,fontSize:'0.7rem'}}>{Number(row.igv||0).toLocaleString("es-PE",{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td style={{padding:'7px 8px',textAlign:'right',color:IOS.label2,fontSize:'0.7rem'}}>{Number(row.inafecta||0).toLocaleString("es-PE",{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:IOS.label,fontSize:'0.72rem'}}>{Number(row.total||0).toLocaleString("es-PE",{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td style={{padding:'7px 8px',textAlign:'center'}}><PillEstado val={row.obsResumen} /></td><td><div className="integra-row-actions economia-actions">
                            <Tooltip title={esNoEnviado(row)?'Ver error de envío':'Ver comprobante'} placement="top">
                              <Box type="button" data-action-tone={esNoEnviado(row)?"orange":"blue"} component="button" aria-label={`${esNoEnviado(row)?'Ver error de envío':'Ver comprobante'} ${row.serie}-${row.numero}`} onClick={()=>verComprobante(row)}
                                sx={{width:26,height:26,borderRadius:'50%',
                                  bgcolor:esNoEnviado(row)?`${IOS.orange}12`:`${IOS.blue}12`,
                                  display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer',
                                  '&:hover':{bgcolor:esNoEnviado(row)?`${IOS.orange}22`:`${IOS.blue}22`}}}>
                                {esNoEnviado(row)
                                  ?<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                  :<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                }
                              </Box>
                            </Tooltip>
                          </div></td></tr>
                      ))
                    }
                  </tbody>
                </table>
              </Box>
              <Paginacion total={datosFiltrados.length} pagina={paginaActual} setPagina={setPagina} rpp={rpp} setRpp={setRpp} />
            </Box>
          }
        </Box>
      </Box>

      <PharmacyDownloadDialog open={modalExportar && !modalConfirmExport} onClose={()=>setModalExportar(false)} title="Exportar libro de ventas" subtitle={datosFiltrados.length+' registros · '+MESES[mes-1]+' '+anho} busy={exportando} options={['excel','pdf'].map(fmt=>({fmt,label:fmt==='excel'?'Descargar Excel':'Descargar PDF',onClick:()=>exportarConFiltros(fmt)}))}>
        {errorAccion && <Alert severity="error">{errorAccion}</Alert>}
          {(() => {
            const filtrosActivos = [];
            if(busqueda.trim()) filtrosActivos.push(`Búsqueda: "${busqueda.trim()}"`);
            if(filtroRapido!=='TODOS') filtrosActivos.push(`Tipo: ${filtroRapido}`);
            if(filtroSerie!=='TODOS') filtrosActivos.push(`Serie: ${filtroSerie}`);
            if(filtroEstado!=='TODOS') filtrosActivos.push(`Estado: ${filtroEstado}`);
            if(filtroAfectacion!=='TODOS') filtrosActivos.push(`Afectación: ${filtroAfectacion}`);
            if(filtroTipoCpe!=='TODOS') filtrosActivos.push(`Tipo CPE: ${OPTS_TIPO_CPE.find(o=>o.value===filtroTipoCpe)?.label||filtroTipoCpe}`);
            if(diaSeleccionado) filtrosActivos.push(`Día: ${diaSeleccionado}`);
            if(filtrosActivos.length===0) return null;
            return (
              <Box sx={{p:1.2,bgcolor:`${IOS.blue}08`,borderRadius:'10px',border:`1px solid ${IOS.blue}25`,mb:2}}>
                <Typography sx={{fontSize:'0.62rem',color:IOS.blue,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',mb:0.4}}>
                  Filtros aplicados
                </Typography>
                <Typography sx={{fontSize:'0.74rem',color:IOS.label,lineHeight:1.5}}>
                  {filtrosActivos.join('  ·  ')}
                </Typography>
              </Box>
            );
          })()}
      </PharmacyDownloadDialog>

      {/* Modal confirmar export con pendientes */}
      <Modal open={!!modalConfirmExport} onClose={()=>!exportando&&setModalConfirmExport(null)} w={420}>
        <ModalHeader title="Hay comprobantes pendientes" onClose={()=>!exportando&&setModalConfirmExport(null)} color={IOS.orange}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} />
        <Box sx={{p:2.5}}>
          {errorAccion && <Alert severity="error" sx={{mb:2}}>{errorAccion}</Alert>}
          <Box sx={{p:1.5,bgcolor:'#FFF8E6',borderRadius:'10px',border:'1px solid #FFE9A0',mb:2}}>
            <Typography sx={{fontSize:'0.8rem',color:'#8B6914',lineHeight:1.6}}>
              Este período tiene <strong>{noEnviados.length} comprobante{noEnviados.length!==1?'s':''}</strong> sin enviar a SUNAT.
              {noEnviadosFueraPlazo.length>0 && <> <strong>{noEnviadosFueraPlazo.length}</strong> ya superó el plazo de {PLAZO_DIAS_SUNAT} días y deberá pasar al período en curso.</>}
            </Typography>
          </Box>
          <Typography sx={{fontSize:'0.8rem',color:IOS.label2,mb:2.5,lineHeight:1.5}}>
            Puedes exportar igual, pero el reporte no reflejará estos comprobantes una vez se regularicen. ¿Deseas continuar?
          </Typography>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>!exportando&&setModalConfirmExport(null)}
              sx={{flex:1,height:38,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',fontWeight:600,color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={()=>ejecutarExportacion(modalConfirmExport)} disabled={exportando}
              sx={{flex:1,height:38,borderRadius:'10px',border:'none',bgcolor:exportando?'#E5E5EA':IOS.orange,cursor:exportando?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:0.8,transition:'all 0.15s'}}>
              {exportando&&<CircularProgress size={12} sx={{color:'white'}} />}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:exportando?'#8E8E93':'white'}}>{exportando?'Exportando...':'Exportar igual'}</Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal Formato Impresión */}
      <Modal open={modalFormato&&!!filaImprimir} onClose={()=>!imprimiendo&&setModalFormato(false)} w={380}>
        <ModalHeader title="Formato de Impresión" onClose={()=>!imprimiendo&&setModalFormato(false)} color={IOS.blue}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>} />
        <Box sx={{p:2.5}}>
          {errorImpresion && <Alert severity="error" sx={{mb:2}}>{errorImpresion}</Alert>}
          {filaImprimir&&(
            <>
              <Typography sx={{fontSize:'0.8rem',color:IOS.label,fontWeight:600,mb:0.3}}>{filaImprimir.tipoCpe} — {filaImprimir.serie}-{filaImprimir.numero}</Typography>
              <Typography sx={{fontSize:'0.74rem',color:IOS.label2,mb:2}}>{filaImprimir.razonSocial}</Typography>
            </>
          )}
          <Box sx={{display:'flex',gap:1.5,mb:1}}>
            {[{fmt:'A4',label:'Formato A4',sub:'Hoja completa',color:IOS.blue},{fmt:'TICKET',label:'Ticket',sub:'80mm / POS',color:IOS.orange}].map(({fmt,label,sub,color})=>(
              <Box key={fmt} component="button" onClick={()=>!imprimiendo&&ejecutarImpresion(fmt)} disabled={imprimiendo}
                sx={{flex:1,py:2.5,borderRadius:'14px',border:`2px solid ${color}30`,bgcolor:`${color}08`,cursor:imprimiendo?'default':'pointer',
                  display:'flex',flexDirection:'column',alignItems:'center',gap:0.8,transition:'all 0.2s',
                  '&:hover':{bgcolor:`${color}14`,borderColor:`${color}60`}}}>
                <Box sx={{width:44,height:44,borderRadius:'12px',bgcolor:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="22" height={fmt==='A4'?28:34} viewBox={fmt==='A4'?"0 0 16 22":"0 0 12 22"} fill="none">
                    {fmt==='A4'
                      ?<><rect x="1" y="1" width="14" height="20" rx="1.5" fill={`${color}20`} stroke={color} strokeWidth="1"/><line x1="4" y1="6" x2="12" y2="6" stroke={color} strokeWidth="1"/><line x1="4" y1="9" x2="12" y2="9" stroke={color} strokeWidth="1"/><line x1="4" y1="12" x2="9" y2="12" stroke={color} strokeWidth="1"/></>
                      :<><rect x="1" y="1" width="10" height="20" rx="1.5" fill={`${color}20`} stroke={color} strokeWidth="1"/><line x1="3" y1="5" x2="9" y2="5" stroke={color} strokeWidth="0.8"/><line x1="3" y1="8" x2="9" y2="8" stroke={color} strokeWidth="0.8"/><line x1="3" y1="11" x2="9" y2="11" stroke={color} strokeWidth="0.8"/></>
                    }
                  </svg>
                </Box>
                <Typography sx={{fontSize:'0.8rem',fontWeight:700,color}}>{label}</Typography>
                <Typography sx={{fontSize:'0.65rem',color:IOS.label2}}>{sub}</Typography>
              </Box>
            ))}
          </Box>
          {imprimiendo&&<Box sx={{display:'flex',justifyContent:'center',mt:1}}><CircularProgress size={20} sx={{color:IOS.blue}} /></Box>}
        </Box>
      </Modal>

      <SalesReceiptDrawer open={panelOpen} onClose={()=>setPanelOpen(false)} row={filaSelec} items={detalleVenta} loading={cargandoDet} error={errorDetalle} onRetry={()=>verComprobante(filaSelec)} onPrint={()=>imprimir(filaSelec)} onDownload={descargarDocOriginal} cdrAvailable={cdrDisponible} downloading={descargandoDoc} documentError={errorDocumento}/>

      {/* Modal No Enviado */}
      <Modal open={modalNoEnviado&&!!filaNoEnviado} onClose={()=>setModalNoEnviado(false)} w={500}>
        <ModalHeader title="Revisar comprobante" onClose={()=>setModalNoEnviado(false)} color={IOS.orange}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} />
        <Box sx={{p:2.5}}>
          {filaNoEnviado&&<section className="sales-error-summary">
            <div className="sales-error-document"><div><span>{tipoCPETexto(filaNoEnviado)}</span><h3>{filaNoEnviado.serie}-{filaNoEnviado.numero}</h3></div><strong>{fmtS(filaNoEnviado.total)}</strong></div>
            <div className="sales-error-client"><span>Cliente</span><strong>{filaNoEnviado.razonSocial||'—'}</strong><small>Documento · {filaNoEnviado.ruc||'—'}</small></div>
            <dl><div><dt>Fecha de emisión</dt><dd>{fmtFecha(filaNoEnviado.periodo)}</dd></div><div><dt>Afectación</dt><dd>{filaNoEnviado.afectacion||'—'}</dd></div></dl>
          </section>}
          {errorEnvio ? <Alert severity="error" action={<button onClick={()=>verComprobante(filaNoEnviado)}>Reintentar</button>}>{errorEnvio}</Alert> : cargandoNoEnviado
            ?<Box sx={{display:'flex',justifyContent:'center',py:3}}><CircularProgress size={24} sx={{color:IOS.orange}} /></Box>
            :detalleNoEnviado
              ?(()=>{
                  const parts=(detalleNoEnviado.logMensaje||'').split('  -  Mensaje de error completo:');
                  const resumen=parts[0].trim(); const detalle=parts[1]?.trim();
                  return (
                    <>
                      <Box className="sales-error-reason">
                        <Typography sx={{fontSize:'0.6rem',color:IOS.orange,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',mb:0.5}}>Motivo</Typography>
                        <Typography sx={{fontSize:'0.84rem',fontWeight:700,color:IOS.label}}>{resumen||'No se registró un motivo adicional.'}</Typography>
                      </Box>
                      {detalle&&(
                        <Box sx={{p:1.5,bgcolor:'#FFF0EF',borderRadius:'12px',border:'1px solid #FFD4D1',whiteSpace:'pre-wrap',overflowWrap:'anywhere',
                          '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:'#FFD4D1',borderRadius:4}}}>
                          <Typography sx={{fontSize:'0.6rem',color:IOS.red,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',mb:0.5}}>Detalle del error</Typography>
                          <Typography sx={{fontSize:'0.72rem',color:IOS.label,fontFamily:'monospace',lineHeight:1.5,wordBreak:'break-word'}}>{detalle}</Typography>
                        </Box>
                      )}
                    </>
                  );
                })()
              :<Box sx={{p:2,bgcolor:IOS.bg,borderRadius:'12px',border:`1px solid ${IOS.separator}`,textAlign:'center'}}>
                  <Typography sx={{fontSize:'0.82rem',color:IOS.label2}}>No se encontró detalle para este comprobante</Typography>
                </Box>
          }
        </Box>
      </Modal>
      <style>{`
  @keyframes fadeInChart {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0);   }
  }
`}</style>
    </>
  );
};

export default LibroVentas;
